// Email Verification Service for Patient Registration
import { supabase } from './supabase'
import { emailNotificationService } from './emailNotifications'

// Generate a 6-digit verification code
export const generateVerificationCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

interface VerificationData {
  code: string
  email: string
  expiresAt: number
  attempts: number
}

const STORAGE_KEY = 'savidna-email-verification'

// Send verification code email via EmailJS
const sendVerificationEmail = async (
  email: string,
  code: string,
): Promise<{ success: boolean; error?: string }> => {
  // Check if EmailJS is configured
  if (!emailNotificationService.isEnabled()) {
    console.warn('⚠️ EmailJS no está configurado, mostrando código en alert')
    return { success: false, error: 'EmailJS no configurado' }
  }

  try {
    const config = emailNotificationService.getConfig()
    if (!config) {
      return { success: false, error: 'No hay configuración de email' }
    }

    // Send email using the existing EmailJS service
    const result = await (emailNotificationService as any).sendEmail({
      to_email: email,
      to_name: 'Nuevo Paciente',
      from_name: config.senderName || 'Savidna',
      subject: `🔐 Código de Verificación - ${config.senderName || 'Savidna'}`,
      patient_name: 'Nuevo Paciente',
      doctor_name: '',
      service_name: 'Registro de Paciente',
      date: new Date().toLocaleDateString('es-DO'),
      time: '',
      duration: '',
      notes: '',
      action_type: 'Verificación de Correo',
      action_color: '#10b981',
      message: `🔐 Tu código de verificación es:\n\n${code}\n\nEste código expira en 10 minutos.\n\nSi no solicitaste este código, ignora este correo.\n\nSaludos,\n${config.senderName || 'Savidna'}`,
    })

    return result
  } catch (error: any) {
    console.error('Error enviando email de verificación:', error)
    return {
      success: false,
      error: error?.message || 'Error al enviar correo',
    }
  }
}

// Send verification code to email and store in database (with localStorage fallback)
export const sendVerificationCode = async (
  email: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Generate code
    const code = generateVerificationCode()
    const expiresAt = new Date()
    expiresAt.setMinutes(expiresAt.getMinutes() + 10) // Code expires in 10 minutes

    // Try to use Supabase first
    try {
      // Delete any existing codes for this email
      await supabase
        .from('email_verification_codes')
        .delete()
        .eq('email', email.toLowerCase().trim())

      // Store code in database
      const { error: insertError } = await supabase
        .from('email_verification_codes')
        .insert({
          email: email.toLowerCase().trim(),
          code: code,
          expires_at: expiresAt.toISOString(),
          verified: false,
          attempts: 0,
        })

      if (insertError) {
        // Log the full error details for debugging
        console.error('❌ Supabase insert error:', insertError)
        console.error('Error details:', {
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
          code: insertError.code,
        })

        // Return the actual error to the user
        return {
          success: false,
          error: `Error de base de datos: ${insertError.message}${insertError.hint ? ` (${insertError.hint})` : ''}`,
        }
      }
    } catch (dbError: any) {
      // Log the full error for debugging
      console.error('❌ Database error:', dbError)
      console.error('Error details:', {
        message: dbError?.message,
        details: dbError?.details,
        hint: dbError?.hint,
        code: dbError?.code,
        name: dbError?.name,
      })

      // Return the actual error instead of falling back silently
      return {
        success: false,
        error: `Error al conectar con la base de datos: ${dbError?.message || 'Error desconocido'}`,
      }
    }

    // Try to send email via EmailJS
    const emailResult = await sendVerificationEmail(email, code)

    if (emailResult.success) {
      console.log('✅ Código de verificación enviado por correo a:', email)
      return { success: true }
    } else {
      // EmailJS failed or not configured - show code in console and alert for testing
      console.log('═══════════════════════════════════════════════════════════')
      console.log('📧 CÓDIGO DE VERIFICACIÓN (EmailJS no disponible)')
      console.log('═══════════════════════════════════════════════════════════')
      console.log(`Para: ${email}`)
      console.log(`Código: ${code}`)
      console.log(`Expira en: 10 minutos`)
      console.log(
        '═══════════════════════════════════════════════════════════\n',
      )

      // Show alert with code for testing when EmailJS is not configured
      alert(
        `⚠️ EmailJS no está configurado\n\nCÓDIGO DE VERIFICACIÓN (solo para pruebas):\n\n${code}\n\nEste código expira en 10 minutos.\n\n💡 Configura EmailJS en Configuración → Notificaciones por Correo para enviar códigos reales.`,
      )

      // Still return success because the code was saved to database
      return {
        success: true,
      }
    }
  } catch (error: any) {
    console.error('❌ Unexpected error sending verification code:', error)
    return {
      success: false,
      error: `Error inesperado: ${error?.message || 'Error al enviar código de verificación'}`,
    }
  }
}

// Verify the code entered by user
export const verifyCode = async (
  email: string,
  code: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Try Supabase first
    try {
      const { data, error } = await supabase
        .from('email_verification_codes')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .eq('verified', false)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (!error && data) {
        // Check if too many attempts
        if (data.attempts >= 5) {
          return {
            success: false,
            error: 'Demasiados intentos. Solicita un nuevo código.',
          }
        }

        // Verify code
        if (data.code !== code.trim()) {
          // Increment attempts
          await supabase
            .from('email_verification_codes')
            .update({ attempts: data.attempts + 1 })
            .eq('id', data.id)

          return {
            success: false,
            error: `Código incorrecto. Intentos restantes: ${5 - data.attempts - 1}`,
          }
        }

        // Mark as verified
        await supabase
          .from('email_verification_codes')
          .update({ verified: true })
          .eq('id', data.id)

        return { success: true }
      }
    } catch (dbError) {
      console.warn(
        'Supabase not available, using localStorage fallback:',
        dbError,
      )
    }

    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY)

    if (!stored) {
      return {
        success: false,
        error: 'Código no encontrado. Solicita uno nuevo.',
      }
    }

    const verificationData: VerificationData = JSON.parse(stored)

    // Check if email matches
    if (verificationData.email !== email.toLowerCase().trim()) {
      return {
        success: false,
        error: 'Código no válido para este correo.',
      }
    }

    // Check if expired
    if (Date.now() > verificationData.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return {
        success: false,
        error: 'Código expirado. Solicita uno nuevo.',
      }
    }

    // Check if too many attempts
    if (verificationData.attempts >= 5) {
      localStorage.removeItem(STORAGE_KEY)
      return {
        success: false,
        error: 'Demasiados intentos. Solicita un nuevo código.',
      }
    }

    // Verify code
    if (verificationData.code !== code.trim()) {
      // Increment attempts
      verificationData.attempts += 1
      localStorage.setItem(STORAGE_KEY, JSON.stringify(verificationData))

      return {
        success: false,
        error: `Código incorrecto. Intentos restantes: ${5 - verificationData.attempts}`,
      }
    }

    // Success - remove code from storage
    localStorage.removeItem(STORAGE_KEY)
    return { success: true }
  } catch (error) {
    console.error('Error verifying code:', error)
    return {
      success: false,
      error: 'Error al verificar código',
    }
  }
}

// Check if email has been verified
export const isEmailVerified = async (email: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('email_verification_codes')
      .select('verified')
      .eq('email', email.toLowerCase().trim())
      .eq('verified', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()

    return !error && data?.verified === true
  } catch {
    return false
  }
}
